import { useEffect, useState, useRef } from "react";
import { Canceler } from "axios";
import {
  requestOAuth2Token,
  AuthSession,
  getValidOAuth2Code,
} from "../../../api/OAuth2Client";
import { useAuthenticatedUserContext } from "../UserProvider";

export function useOAuthToken(authSession: AuthSession | undefined) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const canceller = useRef<Canceler | null>(null);
  const { setUser } = useAuthenticatedUserContext();

  useEffect(() => {
    // not ready to call
    if (!authSession) {
      return;
    }
    // already finished
    if (error != null || !loading) {
      return;
    }
    // Still requesting tokens
    if (canceller.current != null) {
      return;
    }
    // Handle request
    (async () => {
      try {
        const code = getValidOAuth2Code(window.location.href, authSession);
        const [promise, cancel] = requestOAuth2Token(code);
        canceller.current = cancel;
        const user = await promise;
        if (user) {
          setUser(user);
        } else {
          throw new Error("Unexpected token response");
        }
      } catch (err) {
        setError(err);
      } finally {
        canceller.current = null;
        setLoading(false);
      }
    })();
    // Cancel request if we unmount early
    return () => {
      if (canceller.current) {
        canceller.current();
      }
    };
  }, [authSession, error, loading, setUser]);

  return { loading, error };
}
